import React, { useState } from 'react';
import { Alert, Form, Input, Modal, Radio, Select, Space, Typography, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { PoolWallet, WalletPoolProvider } from '../../domain/usdt-providers/walletPoolProvider';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import { normalizeOtp } from '../../core/utils/otp';

const { Text } = Typography;

const errMsg = (e: any) =>
  e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '失敗';

const OPERATION_TYPE_OPTIONS = [
  { value: 'PAYOUT', label: 'PAYOUT(出款)' },
  { value: 'RENTAL', label: 'RENTAL(租用能量)' },
];

/**
 * 從預備錢包池取出一顆錢包 —— 分派成「用戶錢包」或「運營錢包」兩種去向。
 *
 * 跟 RetireActions 那組 modal 一樣的形狀:controlled Modal + Form + 2FA,
 * onOk 送出、失敗只 setErr 顯示在框內(adminRequest 攔截器已經彈過一次紅色
 * message,不重複跳)。
 */
export default function WalletPoolCheckoutModal(props: {
  open: boolean;
  wallet: PoolWallet | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { open, wallet, onClose, onDone } = props;
  const [form] = Form.useForm();
  const [as, setAs] = useState<'user' | 'operation'>('user');
  const [systemId, setSystemId] = useState<string>('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const close = () => {
    form.resetFields();
    setAs('user');
    setSystemId('');
    setErr('');
    onClose();
  };

  const submit = async () => {
    if (!wallet) return;
    const v = await form.validateFields();
    setErr('');
    setLoading(true);
    try {
      const input: any = { as, code: normalizeOtp(v.code) };
      if (as === 'user') {
        input.systemId = systemId;
        input.uid = v.uid;
      } else {
        input.type = v.type;
        input.note = v.note;
      }
      const r = await WalletPoolProvider.checkout(wallet.id, input);
      message.success(`已取出 ${r.address}`);
      close();
      onDone();
    } catch (e: any) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`取出錢包${wallet ? ` —— ${wallet.address}` : ''}`}
      visible={open}
      onCancel={close}
      onOk={submit}
      okText="確認取出"
      okButtonProps={{ danger: true, loading }}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {wallet?.status === 'INCUBATING' && (
          <Alert type="warning" showIcon message="此錢包尚未養熟(未滿出池條件),確定要取出?" />
        )}

        <Form form={form} layout="vertical" initialValues={{ type: 'PAYOUT' }}>
          <Form.Item label="去向">
            <Radio.Group value={as} onChange={(e) => setAs(e.target.value)}>
              <Radio.Button value="user">用戶錢包</Radio.Button>
              <Radio.Button value="operation">出款錢包</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {as === 'user' ? (
            <>
              <Form.Item label="商戶" required>
                <SystemIdSearchList systemId={systemId} onChange={setSystemId} />
              </Form.Item>
              <Form.Item
                name="uid"
                label="用戶 UID"
                rules={[{ required: true, message: '請輸入用戶 UID' }]}
              >
                <Input placeholder="uid" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="type"
                label="運營錢包類型"
                rules={[{ required: true, message: '請選擇類型' }]}
              >
                <Select options={OPERATION_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item name="note" label="備註">
                <Input placeholder="選填" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="code"
            label="驗證碼"
            rules={[{ required: true, pattern: /^\d{6}$/, message: '請輸入驗證碼' }]}
          >
            <Input
              maxLength={6}
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="Google Authenticator"
            />
          </Form.Item>
        </Form>

        {as === 'user' && !systemId && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            請先選擇商戶
          </Text>
        )}

        {err && <Alert type="error" showIcon message={err} />}
      </Space>
    </Modal>
  );
}
